import React, { useState } from "react";
import Swal from "sweetalert2";
import ScrollToTop from "../../ScrollToTop";

const StudentComplaintForm = () => {
  const [formData, setFormData] = useState({
    complainantName: "",
    contactNumber: "",
    email: "",
    preferredContact: [], 
    phoneMessagePermission: "", 
    status: "", 
    programmeOfStudy: "",
    attendanceStart: "",
    attendanceEnd: "",
    complaintDescription: "",
    confirmation: false,
    signature: "",
    dateSigned: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox" && name === "preferredContact") {
      let updated = [...formData.preferredContact];
      if (checked) {
        updated.push(value);
      } else {
        updated = updated.filter((v) => v !== value);
      }
      setFormData({ ...formData, preferredContact: updated });
    } else if (type === "checkbox" && name === "confirmation") {
      setFormData({ ...formData, confirmation: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const createFormattedData = () => {
    const formattedData = new FormData();

    formattedData.append("_subject", "New Student Complaint Form Submission");
    formattedData.append("_captcha", "false");
    formattedData.append("_template", "table");

    Object.entries(formData).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        formattedData.append(key, value.join(", "));
      } else {
        formattedData.append(key, value);
      }
    });

    return formattedData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.confirmation) {
      Swal.fire({
        icon: "error",
        title: "Please confirm the statement",
        text: "You must confirm that the information provided is accurate.",
      });
      return;
    }

    setIsSubmitting(true);

    const formSubmitEndpoint =
      "https://formsubmit.co/sapphirediplomacyleadership@gmail.com";

    const data = createFormattedData();

    Swal.fire({
      title: "Submitting your form...",
      text: "Please wait a moment.",
      icon: "info",
      allowOutsideClick: false,
      showConfirmButton: false,
      timer: 10000,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await fetch(formSubmitEndpoint, {
        method: "POST",
        body: data,
      });

      setTimeout(() => {
        setIsSubmitting(false);

        if (response.ok) {
          Swal.fire({
            title: "Form Submitted!",
            text: "Your complaint has been submitted successfully.",
            icon: "success",
          });
          setFormData({
            complainantName: "",
            contactNumber: "",
            email: "",
            preferredContact: [],
            phoneMessagePermission: "",
            status: "",
            programmeOfStudy: "",
            attendanceStart: "",
            attendanceEnd: "",
            complaintDescription: "",
            confirmation: false,
            signature: "",
            dateSigned: "",
          });
        } else {
          Swal.fire({
            title: "Submission Error",
            text: "There was an issue submitting your form. Please try again later.",
            icon: "error",
          });
        }
      }, 10000);
    } catch (error) {
      setIsSubmitting(false);
      Swal.fire({
        title: "Network Error",
        text: "Please check your connection & try again.",
        icon: "error",
      });
    }
  };

  return (
    <div>

      <div
        className=" bg-cover bg-center py-10 "
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1603573355706-3f15d98cf100?q=80&w=1929&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')",
        }}
      >
        <ScrollToTop />
        <form
          onSubmit={handleSubmit}
          className=" md:p-10 px-2 py-5 mx-2 my-4 bg-blue-200 bg-opacity-70 backdrop-blur-sm border border-black rounded-md max-w-lg md:mx-auto"
        >
          <h2 className="text-2xl font-bold mb-6 text-center">
            Student Complaint Form
          </h2>

          <input
            type="text"
            name="complainantName"
            placeholder="Name of Complainant"
            value={formData.complainantName}
            onChange={handleChange}
            className="w-full p-2 mb-4 border rounded"
            required
          />

          <input
            type="tel"
            name="contactNumber"
            placeholder="Preferred Contact Number"
            value={formData.contactNumber}
            onChange={handleChange}
            className="w-full p-2 mb-4 border rounded"
            required
          />

          <input
            type="email"
            name="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={handleChange}
            className="w-full p-2 mb-4 border rounded"
            required
          />

          <p className="mb-2 font-medium">Preferred Method of Contact:</p>
          <div className="flex gap-6 mb-4">
            {["Telephone", "Email"].map((option) => (
              <label key={option} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="preferredContact"
                  value={option}
                  checked={formData.preferredContact.includes(option)}
                  onChange={handleChange}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>

          <p className="mb-2 font-medium">
            If by phone, may ESBD leave a message?
          </p>
          <div className="flex gap-6 mb-4">
            {["Yes", "No"].map((option) => (
              <label key={option} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="phoneMessagePermission"
                  value={option}
                  checked={formData.phoneMessagePermission === option}
                  onChange={handleChange}
                  required
                />
                <span>{option}</span>
              </label>
            ))}
          </div>

          <p className="mb-2 font-medium">Status:</p>
          <div className="flex gap-6 mb-4">
            {["Current Student", "Former Student"].map((option) => (
              <label key={option} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="status"
                  value={option}
                  checked={formData.status === option}
                  onChange={handleChange}
                  required
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
          <input
            type="text"
            name="programmeOfStudy"
            placeholder="Programme of Study"
            value={formData.programmeOfStudy}
            onChange={handleChange}
            className="w-full p-2 mb-4 border rounded"
            required
          />
          <div className="flex gap-4 mb-4">
            <div className="w-1/2">
              <label className="block mb-1 font-medium">Start Date:</label>
              <input
                type="date"
                name="attendanceStart"
                value={formData.attendanceStart}
                onChange={handleChange}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div className="w-1/2">
              <label className="block mb-1 font-medium">End Date:</label>
              <input
                type="date"
                name="attendanceEnd"
                value={formData.attendanceEnd}
                onChange={handleChange}
                className="w-full p-2 border rounded"
                required
              />
            </div>
          </div>
          <textarea
            name="complaintDescription"
            placeholder="Please describe your complaint in detail..."
            value={formData.complaintDescription}
            onChange={handleChange}
            rows={6}
            className="w-full p-2 mb-4 border rounded resize-y"
            required
          />
          <label className="flex items-center mb-4 space-x-2">
            <input
              type="checkbox"
              name="confirmation"
              checked={formData.confirmation}
              onChange={handleChange}
              required
            />
            <span>
              By submitting this form, I confirm that I am a current or former
              student of the European School of Business & Diplomacy, and the
              information provided is accurate and truthful.
            </span>
          </label>
          <input
            type="text"
            name="signature"
            placeholder="Signature of Complainant ( Enter Initials )"
            value={formData.signature}
            onChange={handleChange}
            className="w-full p-2 mb-4 border rounded"
            required
          />
          <input
            type="date"
            name="dateSigned"
            value={formData.dateSigned}
            onChange={handleChange}
            className="w-full p-2 mb-4 border rounded"
            required
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full px-6 py-2 mt-5 bg-blue-600 text-white rounded hover:bg-blue-700 ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default StudentComplaintForm;
